import {action, computed, observable} from "mobx";
import {UIState} from "data/UIState";
import LiveEventData from "./LiveEventData";
import * as moment from "moment/moment";

import {IResResource} from "shared/ResResource";
import {IResDay} from "shared/ResDay";
import {IResOptions} from "shared/ResOptions";
import {BUNDLE_TYPE_ZOOM_APPOINTMENT} from "shared/BundleType";


export type BundleType = 'booth'|'video'|'mainstage'|'library'|'webinar';
export const BUNDLE_TYPE_BOOTH:BundleType = 'booth';
export const BUNDLE_TYPE_VIDEO:BundleType = 'video';
export const BUNDLE_TYPE_MAINSTAGE:BundleType = 'mainstage';
export const BUNDLE_TYPE_LIBRARY:BundleType = 'library';
export const BUNDLE_TYPE_WEBINAR:BundleType = 'webinar';

export interface IBundle {
  _id:string;
  navName: string;
  fullName: string;
  vimeoVideoId:string;
  presenter: any;
  manager: any;
  managerStatement: string;
  presenterStatement: string;
  paperlessHandouts:any[];
  conversationPost:any;
  vendorBooth?:boolean;
  photo:string;
  subTitle:string;
  cardSubTitle:string;
  category:string;
  type:BundleType;
  htmlContent:string;
  presentationStartTS:Date;
  solutions:string[];
  enableReservations:boolean;
  resResources:IResResource[];
  resDays:IResDay[];
  resOptions:IResOptions;
}

export class Bundle implements IBundle {
  _id:string;
  navName: string;
  fullName: string;
  vimeoVideoId:string;
  presenter: any;
  manager: any;
  managerStatement: string;
  presenterStatement: string;
  paperlessHandouts:any[];
  @observable conversationPost:any;
  vendorBooth?:boolean;
  photo:string;
  subTitle:string;
  cardSubTitle:string;
  category:string;
  type:BundleType;
  htmlContent: string;
  presentationStartTS: Date;

  enableReservations: boolean;
  resResources:IResResource[];
  solutions:string[];
  @observable resDays:IResDay[];
  @observable resOptions:IResOptions;

  @computed get liveEvent() {
    return LiveEventData.liveEventForBundleId(this._id);
  }

  @computed get live() {
    return this.liveEvent ? this.liveEvent.live : false;
  }

  @computed get zoomRegistration() {
    if(UIState.user) {
      return UIState.user.zoomRegistrationForBundleId(this._id);
    } else {
      return null;
    }
  }

  @computed get niceType() {
    return BundleData.niceTypeForBundleType(this.type);
  }

  /**
   * This is called so that comment counts are accurate
   *
   * @param post
   */
  @action updateAnyRelatedPostComments = (post) => {
    if(this.conversationPost) {
      this.conversationPost.comments = post.comments;
    }
  }

  @action update = (json:IBundle) => {
    const keys = Object.keys(json);
    for(let i=0;i<keys.length;i++) {
      this[keys[i]] = json[keys[i]];
    }
  }

  constructor(json:IBundle) {
    this.update(json);
  }

}


class _BundleData {
  @observable private allBundles:Bundle[];
  @observable _selectedBundleId:string;

  @action load = (bundles:IBundle[]) => {
    const allBundles:Bundle[] = [];
    for(let i=0;i<bundles.length;i++) {
      allBundles.push(new Bundle(bundles[i]));
    }
    this.allBundles = allBundles;
  }



  @action clear = () => {
    this.allBundles = [];
    this._selectedBundleId = '';
  }

  @computed get bundlesEnabled(){
    return this.allBundles && this.allBundles.length>0;
  }

  @computed get selectedBundleId() {
    return this._selectedBundleId;
  }

  set selectedBundleId(bundleId) {
    this._selectedBundleId = bundleId;
  }

  @computed get selectedBundle() {
    return this._selectedBundleId ? this.allBundles.find((b)=>b._id===this._selectedBundleId) : null;
  }


  @computed get uiStateHasLoadedFeedData() {
    return UIState.feedData && UIState.feedData.feedLoaded && BundleData.bundlesEnabled;
  }


  // TODO: change vendorBooths to a filter as a result of category
  @computed get vendorBooths() {
    if(this.uiStateHasLoadedFeedData && UIState.feedData.solution.vendorBoothsEnabled) {
      return this.allBundles.filter((bundle)=>bundle.vendorBooth&&bundle.type===BUNDLE_TYPE_BOOTH);
    } else {
      return [];
    }
  }

  // TODO: change booths to a filter as a result of category
  @computed get booths() {
    if(this.uiStateHasLoadedFeedData) {
      return this.allBundles.filter((bundle)=>!bundle.vendorBooth&&bundle.type===BUNDLE_TYPE_BOOTH);
    } else {
      return [];
    }
  }

  @computed get webinars() {
    if(this.uiStateHasLoadedFeedData) {
      const webinars = this.allBundles.filter((bundle)=>bundle.type===BUNDLE_TYPE_WEBINAR);
      if(webinars.length>1) {
        webinars.sort((w1, w2) => {
          const w1Start = w1.presentationStartTS ? w1.presentationStartTS : new Date();
          const w2Start = w2.presentationStartTS ? w2.presentationStartTS : new Date();
          return (moment(w1Start).isBefore(moment(w2Start)) ? -1 : 1);
        });
      }
      return webinars;
    } else {
      return [];
    }
  }

  @computed get zoomAppointmentBundles() {
    if(this.uiStateHasLoadedFeedData) {
      return this.allBundles.filter((bundle)=>!bundle.vendorBooth&&bundle.type===BUNDLE_TYPE_ZOOM_APPOINTMENT);
    } else {
      return [];
    }
  }

  bundleForId = (bundleId:string) => {
    const idx = this.allBundles.findIndex((b)=>b._id===bundleId);
    if(idx>-1) {
      return this.allBundles[idx];
    } else {
      return null;
    }
  }

  niceTypeForBundleType(type) {
      if(BUNDLE_TYPE_WEBINAR===type) {
        return "Webinar";
      } else if(BUNDLE_TYPE_BOOTH===type) {
        return "Booth";
      } else if(BUNDLE_TYPE_MAINSTAGE===type) {
        return "Mainstage Session";
      } else {
        return "Presentation";
      }
  }

}



export const BundleData = new _BundleData();
